import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import inAppMessageService from '../../services/inAppMessageService';
import authService from '../../services/authService';
import InAppMessageModal from './InAppMessageModal';

// Session-level set of dismissed IDs to prevent redundant popups in single SPA session
const sessionDismissedIds = new Set();

/**
 * Returns true when the user is in the middle of the admin onboarding flow
 * and in-app messages should be suppressed until they complete it.
 *
 * Two signals are checked:
 *  1. The current route starts with /onboarding (the multi-step setup wizard)
 *  2. The `first_time_admin_onboarding` flag is present in localStorage —
 *     this is written on signup and removed only when the post-onboarding
 *     tour is dismissed or completed (via clearOnboardingTourStorage).
 */
function isUserOnboarding(pathname) {
  if (pathname && pathname.startsWith('/onboarding')) return true;
  try {
    const flag = localStorage.getItem('first_time_admin_onboarding');
    if (flag === 'true') return true;
  } catch (_) {}
  return false;
}

export default function InAppMessageDelivery() {
  const [activeMessage, setActiveMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const fetchActiveMessage = useCallback(async () => {
    if (!authService.isAuthenticated()) return;

    // Suppress in-app messages while admin onboarding is in progress so that
    // a newly created account does not receive popups before it is set up.
    if (isUserOnboarding(location.pathname)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[InAppMessageDelivery] Suppressed during onboarding.');
      }
      return;
    }

    try {
      const data = await inAppMessageService.getActiveInAppMessage();
      if (process.env.NODE_ENV === 'development') {
        console.debug('[InAppMessageDelivery] Active message check:', data);
      }
      if (data && data.message && !sessionDismissedIds.has(data.message.id)) {
        setActiveMessage(data.message);
        setIsOpen(true);
      } else if (!data?.message && activeMessage) {
        // Message was deactivated or expired
        setActiveMessage(null);
        setIsOpen(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[InAppMessageDelivery] Active check skipped/failed:', err?.message);
      }
    }
  }, [activeMessage, location.pathname]);

  // Initial check on mount + when route changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchActiveMessage();
    }, 500);

    return () => clearTimeout(timer);
  }, [fetchActiveMessage, location.pathname]);

  // Listen for same-window publication, cross-tab storage events, focus, and periodic polling
  useEffect(() => {
    const handleBroadcast = () => {
      fetchActiveMessage();
    };

    const handleStorage = (e) => {
      if (e.key === 'inAppMessageLastBroadcast') {
        fetchActiveMessage();
      }
    };

    window.addEventListener('inAppMessagePublished', handleBroadcast);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleBroadcast);

    // Periodic check every 30 seconds for real-time delivery
    const interval = setInterval(fetchActiveMessage, 30_000);

    return () => {
      window.removeEventListener('inAppMessagePublished', handleBroadcast);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleBroadcast);
      clearInterval(interval);
    };
  }, [fetchActiveMessage]);

  const handleDismiss = async ({ interactedWithCta }) => {
    if (!activeMessage) return;

    const messageId = activeMessage.id;
    sessionDismissedIds.add(messageId);
    setIsOpen(false);
    setActiveMessage(null);

    try {
      await inAppMessageService.dismissInAppMessage(messageId, { interactedWithCta });
    } catch {
      // Silently fail on network error, sessionDismissedIds already prevents reprompt
    }
  };

  if (!isOpen || !activeMessage) return null;

  return (
    <InAppMessageModal
      isOpen={isOpen}
      message={activeMessage}
      onDismiss={handleDismiss}
    />
  );
}
